import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Login from './components/Auth/Login';
import ManagementLayout from './components/Layout/Layout';
import TeacherDashboard from './components/Dashboard/TeacherDashboard';
import StudentDashboard from './components/Dashboard/StudentDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';
import AttendanceMarking from './components/Attendance/AttendanceMarking';
import MarksUpload from './components/Marks/MarksUpload';
import MyClasses from './components/Teacher/MyClasses';
import AttendanceReport from './components/Reports/AttendanceReport';
import AdminStudents from './components/Admin/AdminStudents';
import AdminTeachers from './components/Admin/AdminTeachers';
import ClassFees from './components/Admin/ClassFees';
import FeePayments from './components/Admin/FeePayments';
import StudentAttendance from './components/Student/StudentAttendance';
import StudentMarks from './components/Student/StudentMarks';
import StudentExams from './components/Student/StudentExams';
import AdminClasses from './components/Admin/AdminClasses';
import AdminAcademicYears from './components/Admin/AdminAcademicYears';
import ClassAssignments from './components/Admin/ClassAssignments';
import UserManagement from './components/Admin/UserManagement';
import HallTickets from './components/Admin/HallTickets';
import ExamSchedule from './components/Admin/ExamSchedule';
import Notifications from './components/Admin/Notifications';
import ActivityLogs from './components/Admin/ActivityLogs';
import PendingApprovals from './components/Admin/PendingApprovals';

function PrivateRoute({ children, allowedRoles }) {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  const effectiveAllowedRoles = allowedRoles?.includes('admin')
    ? [...allowedRoles, 'superadmin']
    : allowedRoles;

  if (effectiveAllowedRoles && !effectiveAllowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
}

function DashboardRouter() {
  const { user } = useAuth();

  if (user?.role === 'superadmin' || user?.role === 'admin') {
    return <AdminDashboard />;
  }

  if (user?.role === 'teacher') {
    return <TeacherDashboard />;
  }

  if (user?.role === 'student') {
    return <StudentDashboard />;
  }

  return <Navigate to="/login" />;
}

function ManagementShell({ children, allowedRoles }) {
  return (
    <PrivateRoute allowedRoles={allowedRoles}>
      <ManagementLayout>{children}</ManagementLayout>
    </PrivateRoute>
  );
}

export const managementRoutes = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/dashboard',
    element: (
      <ManagementShell>
        <DashboardRouter />
      </ManagementShell>
    ),
  },
  {
    path: '/teacher',
    element: (
      <ManagementShell allowedRoles={['teacher']}>
        <TeacherDashboard />
      </ManagementShell>
    ),
  },
  {
    path: '/teacher/attendance',
    element: (
      <ManagementShell allowedRoles={['teacher']}>
        <AttendanceMarking />
      </ManagementShell>
    ),
  },
  {
    path: '/teacher/marks',
    element: (
      <ManagementShell allowedRoles={['teacher']}>
        <MarksUpload />
      </ManagementShell>
    ),
  },
  {
    path: '/teacher/classes',
    element: (
      <ManagementShell allowedRoles={['teacher']}>
        <MyClasses />
      </ManagementShell>
    ),
  },
  {
    path: '/teacher/reports',
    element: (
      <ManagementShell allowedRoles={['teacher']}>
        <AttendanceReport />
      </ManagementShell>
    ),
  },
  {
    path: '/student',
    element: (
      <ManagementShell allowedRoles={['student']}>
        <StudentDashboard />
      </ManagementShell>
    ),
  },
  {
    path: '/student/attendance',
    element: (
      <ManagementShell allowedRoles={['student']}>
        <StudentAttendance />
      </ManagementShell>
    ),
  },
  {
    path: '/student/marks',
    element: (
      <ManagementShell allowedRoles={['student']}>
        <StudentMarks />
      </ManagementShell>
    ),
  },
  {
    path: '/student/exams',
    element: (
      <ManagementShell allowedRoles={['student']}>
        <StudentExams />
      </ManagementShell>
    ),
  },
  {
    path: '/admin',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AdminDashboard />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/students',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AdminStudents />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/teachers',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AdminTeachers />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/class-fees',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <ClassFees />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/fee-payments',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <FeePayments />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/attendance',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AttendanceMarking />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/marks',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <MarksUpload />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/reports',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AttendanceReport />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/classes',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AdminClasses />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/academic-years',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <AdminAcademicYears />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/class-assignments',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <ClassAssignments />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/users',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <UserManagement />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/hall-tickets',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <HallTickets />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/pending-approvals',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <PendingApprovals />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/exam-schedule',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <ExamSchedule />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/notifications',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <Notifications />
      </ManagementShell>
    ),
  },
  {
    path: '/admin/activity-logs',
    element: (
      <ManagementShell allowedRoles={['admin']}>
        <ActivityLogs />
      </ManagementShell>
    ),
  },
];
